import type { UnitStatSummaryViewModel } from '@/api/types/UnitStatSummaryViewModel';
import type { UnitStatSummarySplitViewModel } from '@/api/types/UnitStatSummarySplitViewModel';
import type { UnitStatSummaryPolicyPremiumDetailViewModel } from '@/api/types/UnitStatSummaryPolicyPremiumDetailViewModel';

import Accordion from '@mui/material/Accordion';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import {
  Box,
  Card,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  CardContent,
} from '@mui/material';

import { ClassCodeTableCell } from 'src/components/ncrb-table-cells/class-code';

export type UnitStatSplitRow = {
  [K in keyof Pick<
    UnitStatSummarySplitViewModel,
    | 'lastUnitUpdated'
    | 'classCode'
    | 'classDescription'
    | 'estimatedAnnualPayroll'
    | 'ratePer100'
    | 'estimatedAnnualPremium'
  >]-?: string;
};

export type UnitStatPolicyPremiumDetails = {
  [K in keyof Pick<
    UnitStatSummaryPolicyPremiumDetailViewModel,
    'totalSubjectPremium' | 'totalStandardPremium' | 'totalModifiedPremium'
  >]-?: string;
};

export type UnitStatAccordionItem = {
  id: NonNullable<UnitStatSummaryViewModel['id']>;
  summaryDate: string;
  splits: UnitStatSplitRow[];
  policyPremiumDetails: UnitStatPolicyPremiumDetails;
};

type UnitStatAccordionProps = {
  items: UnitStatAccordionItem[];
};

const POLICY_PREMIUM_DETAIL_ORDER: Array<{
  label: string;
  key: keyof UnitStatPolicyPremiumDetails;
}> = [
  { label: 'Total Subject Premium', key: 'totalSubjectPremium' },
  { label: 'Total Standard Premium', key: 'totalStandardPremium' },
  { label: 'Modified Premium', key: 'totalModifiedPremium' },
];

export default function UnitStatAccordion({ items }: Readonly<UnitStatAccordionProps>) {
  return (
    <Box data-testid="unit-stat-accordion-list">
      {items.map((item, accordionIndex) => (
        <Accordion key={item.id} disableGutters >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            data-testid={`unit-stat-accordion-${accordionIndex}-summary`}
            sx={{
              flexDirection: 'row-reverse',
              '& .MuiAccordionSummary-expandIconWrapper': {
                color: 'text.secondary',
                mr: 1,
              }
            }}
          >
            <Typography data-testid={`unit-stat-accordion-${accordionIndex}-summary-date`}>
              {item.summaryDate}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: '1rem', pl: '1rem !important', pr: '1rem !important', backgroundColor: 'background.neutral' }}>
            <Card variant="outlined" sx={{ borderRadius: 2,}}>
              <CardContent sx={{ pl: '1rem', pr: '1rem'}}>
                <Box sx={{ p: 2, borderBottom: '0.0625em solid var(--palette-divider)' }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: 'text.primary' }}
                    data-testid={`unit-stat-accordion-${accordionIndex}-splits-title`}
                  >
                    Splits
                  </Typography>
                </Box>
                <Table size="small" data-testid={`unit-stat-accordion-${accordionIndex}-splits-table`}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Last Unit Updated</TableCell>
                      <TableCell>Class Code</TableCell>
                      <TableCell>Est. annual payroll</TableCell>
                      <TableCell>Rate per $100</TableCell>
                      <TableCell>Est. Annual Premium</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {item.splits.map((split, splitIndex) => (
                      <TableRow key={`${item.id}-split-${splitIndex}`}>
                        <TableCell
                          data-testid={`unit-stat-accordion-${accordionIndex}-split-${splitIndex}-last-unit-updated`}
                        >
                          {split.lastUnitUpdated}
                        </TableCell>
                        <ClassCodeTableCell
                          classCode={split.classCode}
                          description={split.classDescription}
                          dataTestIdPrefix={`unit-stat-accordion-${accordionIndex}-split-${splitIndex}-class-code`}
                        />
                        <TableCell
                          data-testid={`unit-stat-accordion-${accordionIndex}-split-${splitIndex}-annual-payroll`}
                        >
                          {split.estimatedAnnualPayroll}
                        </TableCell>
                        <TableCell
                          data-testid={`unit-stat-accordion-${accordionIndex}-split-${splitIndex}-rate-per-100`}
                        >
                          {split.ratePer100}
                        </TableCell>
                        <TableCell
                          data-testid={`unit-stat-accordion-${accordionIndex}-split-${splitIndex}-annual-premium`}
                          align="right"
                        >
                          {split.estimatedAnnualPremium}
                        </TableCell>
                      </TableRow>
                    ))}
                    {POLICY_PREMIUM_DETAIL_ORDER.map((premiumDetail, detailIndex) => (
                      <TableRow
                        key={`${item.id}-premium-detail-${detailIndex}`}
                        sx={{ '& .MuiTableCell-root': { borderBottom: 'none' } }}
                      >
                        <TableCell sx={{ p: 0 }} />
                        <TableCell sx={{ p: 0 }} />
                        <TableCell sx={{ p: 0 }} />
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 600,
                            p: 0,
                            color: 'text.secondary',
                            py: detailIndex === 0 ? 2 : 1,
                            pb: detailIndex === POLICY_PREMIUM_DETAIL_ORDER.length - 1 ? 2 : 1,
                          }}
                          data-testid={`unit-stat-accordion-${accordionIndex}-premium-detail-${detailIndex}-label`}
                        >
                          {premiumDetail.label}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: 'text.primary',
                            py: detailIndex === 0 ? 2 : 1,
                            pb: detailIndex === POLICY_PREMIUM_DETAIL_ORDER.length - 1 ? 2 : 1,
                          }}
                          data-testid={`unit-stat-accordion-${accordionIndex}-premium-detail-${detailIndex}-value`}
                        >
                          {item.policyPremiumDetails[premiumDetail.key]}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
